import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import CTASection from "@/components/feature/CTASection";
import { useTranslation } from "react-i18next";

export default function AboutPage() {
  const { t } = useTranslation();

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于我们 - 51吃瓜网",
    description: "了解51吃瓜网品牌故事、平台特色与联系方式。正能量不打烊，爆料第一线。",
    url: "http://9mzg.cn/about",
    isPartOf: {
      "@type": "WebSite",
      name: "51吃瓜网",
      url: "http://9mzg.cn/",
    },
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "首页",
        item: "http://9mzg.cn/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "关于我们",
      },
    ],
  };

  const features = t("about.features", { returnObjects: true }) as Array<{
    icon: string;
    title1: string;
    title2: string;
    description: string;
  }>;

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Section */}
        <header className="relative h-[500px] md:h-[600px] flex items-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20office%20team%20working%20on%20digital%20media%2C%20professional%20newsroom%20atmosphere%2C%20warm%20golden%20lighting%2C%20collaborative%20workspace%2C%20editorial%20photography%20style%2C%20dark%20green%20and%20amber%20accents%2C%20professional%20yet%20creative%20composition&width=1920&height=1080&seq=about-hero-bg&orientation=landscape"
            alt="关于51吃瓜网"
            title="关于我们 - 51吃瓜网"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>

          <div className="relative z-10 w-full h-full flex flex-col">
            <div className="flex-1 flex items-center">
              <div className="max-w-7xl mx-auto px-6 md:px-10 w-full">
                <div className="max-w-2xl">
                  <span className="inline-block px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm text-white text-sm font-medium mb-6">
                    {t("about.hero.label")}
                  </span>
                  <h1 className="font-serif font-black text-4xl md:text-6xl lg:text-7xl text-white leading-tight">
                    {t("about.hero.title1")}
                    <br />
                    {t("about.hero.title2")}
                  </h1>
                </div>
              </div>
            </div>

            <div className="pb-16 md:pb-24">
              <div className="max-w-7xl mx-auto px-6 md:px-10">
                <p className="font-serif font-black text-3xl md:text-5xl text-white leading-none">
                  {t("about.hero.title1")} · {t("about.hero.title2")}
                </p>
              </div>
            </div>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Breadcrumb
            items={[
              { label: t("common.breadcrumb.home"), href: "/" },
              { label: t("common.breadcrumb.aboutUs") },
            ]}
          />
        </div>

        {/* Brand Introduction */}
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="flex flex-col lg:flex-row gap-12">
              <div className="flex-1">
                <span className="inline-block px-3 py-1 rounded-full bg-background-200 text-foreground-600 text-xs font-medium mb-6">
                  {t("about.hero.label")}
                </span>
                <h2 className="font-serif font-bold text-3xl md:text-4xl text-foreground-950 leading-tight mb-6">
                  {t("about.hero.title1")}
                  <br />
                  {t("about.hero.title2")}
                </h2>
                <p className="text-foreground-500 leading-relaxed text-base">
                  {t("about.hero.description")}
                </p>
              </div>

              <div className="lg:w-[460px] flex-shrink-0">
                <div className="bg-accent-700 rounded-2xl p-10 text-white relative overflow-hidden">
                  <div className="absolute top-6 left-6">
                    <span className="text-xs text-white/40 uppercase tracking-wider">
                      {t("about.hero.missionTitle")}
                    </span>
                  </div>
                  <div className="absolute top-6 right-6 flex gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-white/30"></span>
                    <span className="w-1.5 h-1.5 rounded-full bg-white/30"></span>
                    <span className="w-1.5 h-1.5 rounded-full bg-white/30"></span>
                  </div>
                  <div className="mt-10 mb-8">
                    <p className="text-lg md:text-xl leading-relaxed font-serif">
                      {t("about.hero.missionText")}
                    </p>
                  </div>
                  <div className="flex items-center justify-between pt-5 border-t border-white/20">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                        <span className="text-white font-bold text-sm">瓜</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium">{t("about.hero.founderName")}</p>
                        <p className="text-xs text-white/60">{t("about.hero.founderTitle")}</p>
                      </div>
                    </div>
                    <button className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors duration-200 cursor-pointer">
                      <i className="ri-arrow-right-up-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="bg-background-100 py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="text-center mb-14">
              <span className="inline-block px-4 py-1.5 rounded-full border border-accent-300 text-accent-700 text-sm font-medium mb-4">
                {t("about.featuresSection.label")}
              </span>
              <h2 className="font-serif font-bold text-3xl md:text-4xl text-foreground-950 mb-3">
                {t("about.featuresSection.title1")}
                <br />
                {t("about.featuresSection.title2")}
              </h2>
              <p className="text-foreground-500 max-w-xl mx-auto">
                {t("about.featuresSection.description")}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {Array.isArray(features) &&
                features.map((feature, idx) => (
                  <div
                    key={idx}
                    className="group bg-white rounded-lg p-6 transition-all duration-300 hover:bg-accent-500 hover:text-white cursor-pointer"
                  >
                    <div className="w-12 h-12 rounded-lg bg-background-200 group-hover:bg-white/20 flex items-center justify-center mb-5 transition-colors duration-300">
                      <i
                        className={`${feature.icon} text-xl text-foreground-700 group-hover:text-white transition-colors duration-300`}
                      ></i>
                    </div>
                    <h3 className="font-bold text-lg text-foreground-950 group-hover:text-white mb-2 leading-tight">
                      {feature.title1}
                      <br />
                      {feature.title2}
                    </h3>
                    <p className="text-foreground-500 text-sm group-hover:text-white/80 mb-4 leading-relaxed">
                      {feature.description}
                    </p>
                    <span className="inline-flex items-center gap-1 text-primary-600 text-sm font-medium group-hover:text-white/90 whitespace-nowrap">
                      了解更多
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                ))}
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <CTASection
          title={t("about.contactSection.title")}
          subtitle={t("about.contactSection.subtitle")}
          buttonText={t("about.contactSection.button")}
          variant="about"
        />

        <Footer />
      </div>
    </>
  );
}